import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { navigateTo } from '../../../libs/history';
import { getTrackedInternalLinkUrl } from '../../../libs/tracking';
import { formatCountdownMsTime, formatMsShortNoHours } from '../../../libs/utils';
import { useSiteConfig } from '../../../scripts/selectors/global';
import { useTrackedImpression } from '../../../scripts/hooks/useTrackedImpression';
import { asset, scaledAssetUrl } from '../../../services/assetResolverService';
import { useMedia } from '../../grid/hooks';
import { Link } from '../../link';
import CountdownTimer from '../../presentational/countdownTimer';

type TMdiReplayRaceMnS1UpsellProps = {
  className?: string;
};

type TReplayTarget = {
  slug: string;
  name: string;
  expansionId: number;
  analyticsKey: string;
  /** Mandatory's deathless Group A clear */
  mythicLevel: number;
  clearTimeMs: number;
};

const NAMESPACE = 'dyn_mdi_replay_race_mn_s1_upsell';
const ANALYTICS_PREFIX = 'mdi_replay_race_mn_s1_upsell';

const EVENT_LANDING_URL = '/events/mdi-dungeon-replay-race-mn-1';
const REGISTER_URL = '/events/mdi-dungeon-replay-race-mn-1/rules';
const ENDS_AT = '2026-06-09T15:00:00Z'; // US weekly reset (Tue 8 AM PDT)

// Reuse the MDI Midnight S1 event mark we already ship for the Time Trials upsell sibling.
const EVENT_LOGO_PATH = '/images/events/expansion11/mdi-midnight-season-1/mdi_logo.png';
const MANDATORY_LOGO_PATH = '/images/upsells/mdi-replay-race-mn-s1/mandatory.png';

// Mandatory's deathless Group A clears — bracket replay restrictions on event mdi-dungeon-replay-race-mn-1.
const REPLAY_TARGETS: TReplayTarget[] = [
  {
    slug: 'magisters-terrace',
    name: "Magisters' Terrace",
    expansionId: 11,
    analyticsKey: 'magisters_terrace',
    mythicLevel: 17,
    clearTimeMs: 887_890
  },
  {
    slug: 'seat-of-the-triumvirate',
    name: 'Seat of the Triumvirate',
    expansionId: 6,
    analyticsKey: 'seat_of_the_triumvirate',
    mythicLevel: 16,
    clearTimeMs: 902_239
  },
  {
    slug: 'pit-of-saron',
    name: 'Pit of Saron',
    expansionId: 2,
    analyticsKey: 'pit_of_saron',
    mythicLevel: 17,
    clearTimeMs: 859_764
  },
  {
    slug: 'maisara-caverns',
    name: 'Maisara Caverns',
    expansionId: 11,
    analyticsKey: 'maisara_caverns',
    mythicLevel: 15,
    clearTimeMs: 744_930
  },
  {
    slug: 'nexuspoint-xenas',
    name: 'Nexus-Point Xenas',
    expansionId: 11,
    analyticsKey: 'nexus_point_xenas',
    mythicLevel: 16,
    clearTimeMs: 748_760
  },
  {
    slug: 'skyreach',
    name: 'Skyreach',
    expansionId: 5,
    analyticsKey: 'skyreach',
    mythicLevel: 17,
    clearTimeMs: 775_507
  }
];

type TUpsellTranslations = {
  header: string;
  countdownLabel: string;
  countdownCompleted: string;
  headline: string;
  button: string;
  secondaryLink: string;
  logoAlt: string;
  mandatoryAlt: string;
};

const EN_TRANSLATIONS: TUpsellTranslations = {
  header: 'MDI Dungeon Replay Race Live Now',
  countdownLabel: 'Ends in',
  countdownCompleted: 'Completed',
  headline:
    "Can you clear <0>{{dungeon}}</0> faster than Mandatory's <1>{{threshold}}</1>? Put up your best times and earn prizes!",
  button: 'COMPETE NOW',
  secondaryLink: 'View Live Leaderboards',
  logoAlt: 'MDI Midnight Season 1 logo',
  mandatoryAlt: 'Mandatory team logo'
};

const UPSELL_TRANSLATIONS: Record<string, TUpsellTranslations> = {
  en: EN_TRANSLATIONS,
  'en-us': EN_TRANSLATIONS
};

const panelStyle: React.CSSProperties = {
  border: '2px solid #56caff',
  borderRadius: '0.25rem',
  backgroundColor: '#08142b',
  color: '#fff',
  cursor: 'pointer',
  columnGap: '0.75rem',
  backgroundSize: 'cover',
  backgroundPosition: 'top center',
  backgroundRepeat: 'no-repeat'
};

const logoWrapStyle: React.CSSProperties = {
  width: 104,
  minWidth: 104,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '-0.2rem'
};

const logoStyle: React.CSSProperties = {
  display: 'block',
  width: 96,
  height: 96,
  objectFit: 'contain',
  filter: 'drop-shadow(0 6px 16px rgba(0, 0, 0, 0.28))'
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.625rem',
  flexWrap: 'wrap',
  marginBottom: '0.3rem'
};

const headerStyle: React.CSSProperties = {
  color: '#8fdfff',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem'
};

const liveDotStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: '0.55em',
  // FA custom props for fa-beat-fade
  ['--fa-beat-fade-opacity' as any]: '0.35',
  ['--fa-beat-fade-scale' as any]: '1.4',
  ['--fa-animation-duration' as any]: '1.4s',
  textShadow: '0 0 8px rgba(239, 68, 68, 0.7)'
};

const countdownStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.2rem 0.55rem',
  borderRadius: '999px',
  border: '1px solid rgba(96, 209, 255, 0.45)',
  background: 'rgba(7, 15, 40, 0.82)',
  color: '#8fdfff',
  fontSize: '0.8125rem',
  fontWeight: 'bold'
};

const contentStyle: React.CSSProperties = {
  maxWidth: '40rem'
};

const headlineRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  maxWidth: '40rem'
};

const headlineTextStyle: React.CSSProperties = {
  lineHeight: 1.18,
  flex: '1 1 auto',
  minWidth: 0
};

const mandatoryLogoStyle: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 6,
  objectFit: 'contain',
  flexShrink: 0,
  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.55))'
};

const dungeonTextStyle: React.CSSProperties = {
  color: '#ffcc5c'
};

const thresholdTextStyle: React.CSSProperties = {
  color: '#ffcc5c'
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  alignItems: 'stretch'
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#ce9908'
};

const supportLinkStyle: React.CSSProperties = {
  color: '#8fdfff',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  textAlign: 'center',
  textDecoration: 'none'
};

const ctaButtonClassName = [
  'slds-button',
  'slds-button--raiderio',
  'text-white',
  'slds-p-vertical--x-small',
  'slds-p-horizontal--small',
  'text-bold',
  'rio-text--title',
  'slds-text-heading--small',
  'text-nowrap'
].join(' ');

function formatThresholdTime(ms: number) {
  // Ceil to the next whole second so the displayed mm:ss matches Mandatory's clear without underselling
  // a partial-second tail (e.g. 12:55.51 reads "12:56", not "12:55").
  const rounded = Math.ceil(ms / 1000) * 1000;
  return formatMsShortNoHours(rounded);
}

function getDungeonBackgroundUrl(target: TReplayTarget) {
  return asset(`/images/dungeons/expansion${target.expansionId}/base/${target.slug}.jpg`);
}

export function MdiReplayRaceMnS1Upsell(props: TMdiReplayRaceMnS1UpsellProps) {
  const { t, i18n } = useTranslation();
  const siteConfig = useSiteConfig();
  const isMobile = useMedia(['(max-width: 767px)'], [true], false);
  const selectedTarget = REPLAY_TARGETS[siteConfig.seed % REPLAY_TARGETS.length];
  const [isCountdownComplete, setIsCountdownComplete] = useState(() => Date.parse(ENDS_AT) <= Date.now());

  Object.entries(UPSELL_TRANSLATIONS).forEach(([lang, translations]) => {
    if (!i18n.hasResourceBundle(lang, NAMESPACE)) {
      i18n.addResourceBundle(lang, NAMESPACE, translations);
    }
  });

  const panelImpressionRef = useTrackedImpression<HTMLDivElement>({
    onImpression: () => {
      RIO_TRACK_EVENT('event', 'upsell_impression', {
        content_type: 'upsell',
        content_id: `${ANALYTICS_PREFIX}:${selectedTarget.analyticsKey}`,
        destination_path: EVENT_LANDING_URL
      });
    }
  });

  const teaserImageUrl = getDungeonBackgroundUrl(selectedTarget);
  const panelBackgroundStyle = {
    ...panelStyle,
    backgroundImage: `linear-gradient(90deg, rgba(5, 10, 28, 0.96) 0%, rgba(24, 18, 69, 0.9) 38%, rgba(8, 20, 43, 0.94) 100%), url(${scaledAssetUrl(
      teaserImageUrl,
      {
        width: 1600,
        height: 360,
        quality: 86
      }
    )})`
  };
  const panelResponsiveStyle = {
    ...panelBackgroundStyle,
    justifyContent: isMobile ? 'center' : undefined,
    rowGap: isMobile ? '0.375rem' : undefined
  };
  const logoWrapResponsiveStyle = {
    ...logoWrapStyle,
    marginLeft: isMobile ? 0 : logoWrapStyle.marginLeft
  };
  const contentResponsiveStyle: React.CSSProperties = {
    ...contentStyle,
    textAlign: isMobile ? 'center' : 'left',
    margin: isMobile ? '0 auto' : undefined
  };
  const headerRowResponsiveStyle = {
    ...headerRowStyle,
    justifyContent: isMobile ? 'center' : 'flex-start'
  };
  const headlineRowResponsiveStyle: React.CSSProperties = {
    ...headlineRowStyle,
    margin: isMobile ? '0 auto' : undefined,
    justifyContent: isMobile ? 'center' : 'flex-start'
  };
  const headlineTextResponsiveStyle: React.CSSProperties = {
    ...headlineTextStyle,
    textAlign: isMobile ? 'center' : 'left'
  };
  const buttonColumnStyle: React.CSSProperties = {
    width: isMobile ? '100%' : undefined,
    textAlign: isMobile ? 'center' : undefined
  };
  const buttonGroupResponsiveStyle = {
    ...buttonGroupStyle,
    alignItems: isMobile ? 'center' : 'stretch'
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    RIO_TRACK_EVENT('event', 'internal_click', {
      link_url: getTrackedInternalLinkUrl(EVENT_LANDING_URL),
      link_text: t(`${NAMESPACE}:header`),
      link_classes: e.currentTarget.className || '',
      link_id: `${ANALYTICS_PREFIX}:panel:${selectedTarget.analyticsKey}`
    });

    navigateTo(EVENT_LANDING_URL, e);
  };

  return (
    <div className={props.className}>
      <div
        ref={panelImpressionRef}
        className="slds-p-around--small slds-grid slds-grid--vertical-align-center slds-wrap slds-medium-nowrap"
        onClick={handlePanelClick}
        style={panelResponsiveStyle}
      >
        <div className="slds-col slds-shrink-none slds-align--absolute-center rio-m-top-mobi--small">
          <span style={logoWrapResponsiveStyle}>
            <img
              src={asset(EVENT_LOGO_PATH, { width: 224, height: 224, quality: 92 })}
              alt={t(`${NAMESPACE}:logoAlt`)}
              width={112}
              height={112}
              style={logoStyle}
            />
          </span>
        </div>
        <div className="slds-col rio-m-top-mobi--small slds-p-horizontal--small" style={contentResponsiveStyle}>
          <div style={headerRowResponsiveStyle}>
            <span style={headerStyle}>
              <i className="fa fa-circle fa-beat-fade" style={liveDotStyle} aria-hidden />
              {t(`${NAMESPACE}:header`)}
            </span>
            <span style={countdownStyle}>
              <i className="fa fa-stopwatch" />
              {isCountdownComplete ? (
                <span className="text-white">{t(`${NAMESPACE}:countdownCompleted`)}</span>
              ) : (
                <>
                  <span>{t(`${NAMESPACE}:countdownLabel`)}</span>
                  <CountdownTimer
                    className="text-white"
                    to={ENDS_AT}
                    formatter={formatCountdownMsTime}
                    onComplete={() => setIsCountdownComplete(true)}
                    tag="span"
                  />
                </>
              )}
            </span>
          </div>
          <div style={headlineRowResponsiveStyle}>
            <img
              src={asset(MANDATORY_LOGO_PATH, { width: 96, height: 96 })}
              alt={t(`${NAMESPACE}:mandatoryAlt`)}
              width={48}
              height={48}
              style={mandatoryLogoStyle}
            />
            <span className="slds-text-heading--medium text-bold" style={headlineTextResponsiveStyle}>
              <Trans
                i18nKey={`${NAMESPACE}:headline`}
                values={{
                  dungeon: selectedTarget.name,
                  threshold: formatThresholdTime(selectedTarget.clearTimeMs)
                }}
                components={[<b style={dungeonTextStyle} />, <b style={thresholdTextStyle} />]}
              />
            </span>
          </div>
        </div>
        <div
          className="slds-col slds-grow slds-p-vertical--small slds-p-right--small slds-align--absolute-center rio-m-top-mobi--small"
          style={buttonColumnStyle}
        >
          <div style={buttonGroupResponsiveStyle}>
            <Link
              className={ctaButtonClassName}
              to={REGISTER_URL}
              as={`${ANALYTICS_PREFIX}:button:${selectedTarget.analyticsKey}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
              style={primaryButtonStyle}
            >
              {t(`${NAMESPACE}:button`)}
            </Link>
            <Link
              to={EVENT_LANDING_URL}
              as={`${ANALYTICS_PREFIX}:leaderboards`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
              style={supportLinkStyle}
            >
              {t(`${NAMESPACE}:secondaryLink`)}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
